import { trackArtifactEdit } from "@/components/artifacts/view/pendingArtifactEdits.ts";
import { confirmationToast, errorToast } from "@/components/ui/toast.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import type { LocationSuggestion } from "@/lib/places.ts";
import { deleteUserLocation, updateUserLocation } from "@/lib/user.ts";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

/** The two addresses okthx plans around. */
export type SavedAddressType = "home" | "work";

/** What each address is called wherever it's named to the user. */
export const SAVED_ADDRESS_LABELS: Record<SavedAddressType, string> = {
  home: "Home",
  work: "Work",
};

/** Kept from the autocomplete pick so the address doesn't need re-geocoding. */
export type SavedAddressCoordinates = {
  latitude?: number;
  longitude?: number;
};

/**
 * The coordinates to save with an address, if the address *is* the pick.
 *
 * A field can be typed into after a suggestion was chosen, and the suggestion's
 * coordinates then describe somewhere the text no longer names. Sending them
 * anyway would pin the user's home to the last place they happened to click, so
 * they only ride along on an exact match.
 */
export function matchedCoordinates(
  suggestion: LocationSuggestion | null,
  address: string,
): SavedAddressCoordinates | undefined {
  if (!suggestion || (suggestion.address || suggestion.name) !== address) return undefined;
  return {
    latitude: suggestion.location?.latitude,
    longitude: suggestion.location?.longitude,
  };
}

/**
 * The user's home and work addresses, and the one way to change them.
 *
 * Shared rather than reimplemented per surface. Settings and the omnibox picker
 * card ask the same question, and the answer carries more than a string: a save
 * has to invalidate the user context *and* the calendar grid (a changed home
 * address moves the effective timezone the grid renders event times in), a
 * delete has to offer an undo, and a coordinate pair from the autocomplete has
 * to ride along when it matches what was typed. Two copies of that drift.
 *
 * Each address commits on its own, with no card-level save gating the pair, so
 * filling in home and walking away keeps home. That is the behaviour every
 * surface needs, so it belongs here rather than in each of them.
 *
 * Every write registers with {@link trackArtifactEdit}. Both picker surfaces —
 * the omnibox card and the canvas one — sit next to a Done that POSTs and has
 * the server read these addresses straight back, and Done blurs the field, which
 * is what starts the save. Without registering, the two requests race and the
 * shorter server path wins: the agent resumes naming the address the user just
 * replaced (the shape of BIG-6261). `fireArtifactAction` already awaits
 * `flushPendingArtifactEdits`; this is its other half. Settings registers too and
 * has no action to block, which costs nothing — the set drains as the request
 * settles.
 */
export function useSavedAddresses() {
  const { userContext, isLoading, mutate: invalidateUserContext } = useCurrentUserContext();
  const queryClient = useQueryClient();
  // A set rather than one "which type is saving": home and work are written
  // independently, so the two can overlap, and a single slot would let whichever
  // finished first re-enable the row still waiting on its own request.
  const [savingTypes, setSavingTypes] = useState<ReadonlySet<SavedAddressType>>(() => new Set());

  const addresses = useMemo(() => {
    const locations = userContext?.user.userContact.locations ?? [];
    return {
      home: locations.find((l) => l.type === "home")?.value || "",
      work: locations.find((l) => l.type === "work")?.value || "",
    };
  }, [userContext]);

  const isSaving = useCallback((type: SavedAddressType) => savingTypes.has(type), [savingTypes]);

  const setSaving = useCallback((type: SavedAddressType, saving: boolean) => {
    setSavingTypes((prev) => {
      const next = new Set(prev);
      if (saving) next.add(type);
      else next.delete(type);
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    await invalidateUserContext();
    // A changed home address moves the effective timezone, and the grid renders
    // event times in it — without this the calendar keeps the old offset.
    await queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
  }, [invalidateUserContext, queryClient]);

  /**
   * Write one address. Reports whether it landed, so a caller that wants to
   * leave edit mode only on success can wait for the answer; the failure is
   * already surfaced as a toast either way.
   */
  const save = useCallback(
    async (
      type: SavedAddressType,
      address: string,
      coordinates?: SavedAddressCoordinates,
    ): Promise<boolean> => {
      const label = SAVED_ADDRESS_LABELS[type];
      setSaving(type, true);
      try {
        await trackArtifactEdit(updateUserLocation({ value: address, type, ...coordinates }));
        await refresh();
        return true;
      } catch (error) {
        reportAddressFailure(error, "save", type);
        errorToast(`Failed to update ${label.toLowerCase()} location. Please try again.`);
        return false;
      } finally {
        setSaving(type, false);
      }
    },
    [refresh, setSaving],
  );

  /**
   * Clear one address, with an undo. The restore writes back the value alone:
   * the coordinates that came with the original pick aren't held anywhere the
   * undo can reach, and re-geocoding the address is what the backend does with
   * a bare value anyway.
   */
  const remove = useCallback(
    async (type: SavedAddressType): Promise<boolean> => {
      const label = SAVED_ADDRESS_LABELS[type];
      const valueToRestore = addresses[type];
      setSaving(type, true);
      try {
        await trackArtifactEdit(deleteUserLocation(type));
        await refresh();
        confirmationToast(`${label} address removed.`, undefined, {
          actionText: "Undo",
          onAction: () => {
            void (async () => {
              try {
                await trackArtifactEdit(updateUserLocation({ value: valueToRestore, type }));
                await refresh();
              } catch (error) {
                reportAddressFailure(error, "undoDelete", type);
                errorToast(`Failed to restore ${label.toLowerCase()} address.`);
              }
            })();
          },
          actionStatsigName: `settings-${type}-undo-delete`,
        });
        return true;
      } catch (error) {
        reportAddressFailure(error, "remove", type);
        errorToast(`Failed to remove ${label.toLowerCase()} address. Please try again.`);
        return false;
      } finally {
        setSaving(type, false);
      }
    },
    [addresses, refresh, setSaving],
  );

  return {
    addresses,
    isLoading,
    /** Whether this address in particular is mid-write, so only its row disables. */
    isSaving,
    save,
    remove,
  };
}

/** Toasts tell the user; Sentry keeps the stack trace for whoever gets paged. */
const reportAddressFailure = (error: unknown, op: string, type: SavedAddressType) => {
  Sentry.captureException(error, {
    tags: { component: "useSavedAddresses", function: op },
    extra: { locationType: type },
  });
};
